import type { Role } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  IconButton,
  NoPermissionState,
  PageHeader,
  RowActions,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Eye, Pencil, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan } from '@/features/auth/session';
import { RoleFormDrawer } from '@/features/roles/RoleFormDrawer';
import { permissionCatalogQueryOptions, rolesQueryOptions } from '@/features/roles/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';

export const Route = createFileRoute('/_app/admin/roles')({ component: RolesPage });

function RolesPage() {
  const { t } = useTranslation(['roles', 'common', 'errors']);
  const canRead = useCan('role.read');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRole, setDrawerRole] = useState<Role | undefined>(undefined);

  const rolesQuery = useQuery({ ...rolesQueryOptions(), enabled: canRead });
  const catalogQuery = useQuery({ ...permissionCatalogQueryOptions(), enabled: canRead });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const roles = rolesQuery.data ?? [];

  const openRole = (role?: Role) => {
    setDrawerRole(role);
    setDrawerOpen(true);
  };

  return (
    <Container>
      <PageHeader
        title={t('roles:title')}
        subtitle={t('roles:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('roles:title') },
        ]}
        actions={
          <Can permission="role.manage">
            <Button variant="primary" onClick={() => openRole(undefined)}>
              <Plus className="ico" aria-hidden="true" />
              {t('roles:create')}
            </Button>
          </Can>
        }
      />

      <Card>
        <CardHeader
          title={t('roles:title')}
          icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
        />

        {rolesQuery.isPending ? (
          <TableSkeleton rows={6} columns={5} label={t('common:states.loading')} />
        ) : rolesQuery.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(rolesQuery.error, t)}
            traceId={errorTraceId(rolesQuery.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void rolesQuery.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : roles.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="ico ico-24" aria-hidden="true" />}
            title={t('roles:emptyTitle')}
            description={t('roles:emptyBody')}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>{t('roles:columns.role')}</Th>
                  <Th>{t('roles:columns.code')}</Th>
                  <Th>{t('roles:columns.type')}</Th>
                  <Th align="right">{t('roles:columns.permissions')}</Th>
                  <Th align="right">{t('roles:columns.users')}</Th>
                  <Th align="right">
                    <span className="sr-only">{t('roles:columns.actions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <Tr key={role.id}>
                    <Td strong>
                      <button
                        type="button"
                        className="link-quiet"
                        style={{
                          background: 'none',
                          border: 0,
                          padding: 0,
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                        onClick={() => openRole(role)}
                      >
                        {role.name}
                      </button>
                      {role.description ? (
                        <span className="cell-user__sub">{role.description}</span>
                      ) : null}
                    </Td>
                    <Td className="cell-id">{role.code}</Td>
                    <Td>
                      <Badge tone={role.isSystem ? 'neutral' : 'violet'}>
                        {role.isSystem ? t('roles:system') : t('roles:custom')}
                      </Badge>
                    </Td>
                    <Td align="right" numeric>
                      {role.permissions.length}
                    </Td>
                    <Td align="right" numeric>
                      {role.usersCount}
                    </Td>
                    <Td align="right">
                      <RowActions>
                        <IconButton
                          label={`${role.isSystem ? t('common:actions.search') : t('common:actions.edit')} — ${role.name}`}
                          onClick={() => openRole(role)}
                        >
                          {role.isSystem ? (
                            <Eye className="ico ico-16" aria-hidden="true" />
                          ) : (
                            <Pencil className="ico ico-16" aria-hidden="true" />
                          )}
                        </IconButton>
                      </RowActions>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <RoleFormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        catalog={catalogQuery.data ?? []}
        {...(drawerRole ? { role: drawerRole } : {})}
      />
    </Container>
  );
}
